import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useItem, useItemReturnReasons, useItemWarehousePicking } from '@/hooks/item';
import { ItemReturnFromWarehouseModal } from '@/types/modal';
import { DatePicker, Dropdown, Input } from '@/components/molecules/common/Form';
import { CopyModal, ErrorModal, LoadingModal, StandardModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';

const TriggerByWarehouseErrorCode = 'SHOULD_DISPLAY_TRIGGER_BY_WAREHOUSE';
const View: React.FC<{ modal: ItemReturnFromWarehouseModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const SellerReturnOrBuyerSendOutId = 2;
  const currentTime = dayjs(new Date());
  const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const itemR = useItem(item.productId, false);
  const submitR = useItemWarehousePicking(item.productId);
  const reasonsR = useItemReturnReasons('warehouse');

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  useEffect(() => {
    if (reasonsR.kind === 'Loaded' && reasonsR.data.data.length > 0) {
      setReason(String(reasonsR.data.data[0].id));
    }
  }, [reasonsR.kind]);

  if (reasonsR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.item_return_from_warehouse.title')}
        onClose={onClose}
      />
    );
  } else if (reasonsR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_return_from_warehouse.title')}
        message={t('modal.item_return_from_warehouse.reasons_error')}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Error') {
    if (submitR.error.kind == 'ServerError' && submitR.error.errorCode === TriggerByWarehouseErrorCode) {
      const body = (
        <div className={styles.containerGap}>
          <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
          <div
            className={styles.text1rem}
            dangerouslySetInnerHTML={{
              __html: t('modal.item_return_from_warehouse.trigger_by_warehouse', {
                formLink: getFormLink(
                  'https://docs.google.com/spreadsheets/d/1SYv1djCeRDw6rlW6Qt-iL4NRAHbAG4FXr6BQdtIWA-4/edit#gid=75788802',
                ),
              }),
            }}
          />
        </div>
      );
      return (
        <StandardModal
          title={t('modal.item_return_from_warehouse.title')}
          onClose={onClose}
          onSubmit={() => submitR}
          submittable={false}
          body={body}
          containerStyle={{ maxWidth: 650 }}
          closeLabel={t('common.ok').toUpperCase()}
          ignoreSubmitLabel={true}
          modalName="item_return_from_warehouse_trigger_by_warehouse"
        />
      );
    }
    return (
      <ErrorModal
        title={t('modal.item_return_from_warehouse.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <CopyModal
        title={t('modal.item_return_from_warehouse.picking_reference_number')}
        beforeInfo={
          <div className={styles.paddingY1rem}>{t('modal.item_return_from_warehouse.picking_reference_generated')}</div>
        }
        afterInfo={<br />}
        textToCopy={String(submitR.data.data.pickingId)}
        onClose={onClose}
      />
    );
  }

  const reasons: { label: string; value: string }[] = [];
  reasonsR.data.data.forEach((element) => {
    if (element.id == SellerReturnOrBuyerSendOutId) {
      reasons.push(
        { label: t('modal.item_return_from_warehouse.seller_return'), value: String(-1) },
        { label: t('modal.item_return_from_warehouse.buyer_sendout'), value: String(element.id) },
      );
    } else {
      reasons.push({ label: element.name, value: String(element.id) });
    }
  });

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('modal.item_return_from_warehouse.item')} #${item.productId}`}</div>
      <Dropdown
        required={true}
        disabled={false}
        label={t('modal.item_return_from_warehouse.request_reason')}
        value={reason}
        onSelect={setReason}
        items={reasons}
      />
      <DatePicker
        required={true}
        disabled={false}
        label={t('modal.item_return_from_warehouse.return_date')}
        min={currentTime}
        onChange={setDueDate}
        value={dueDate || undefined}
      />
      <Input
        disabled={false}
        label={t('modal.item_return_from_warehouse.request_details')}
        value={comment}
        multiline={true}
        rows={4}
        onChange={setComment}
      />
    </div>
  );

  return (
    <StandardModal
      title={t('modal.item_return_from_warehouse.title')}
      onClose={onClose}
      onSubmit={() =>
        submitR.mutate({
          dueDate: dayjs(dueDate).format('YYYY-MM-DD'),
          reasonId: Number(reason) != -1 ? Number(reason) : SellerReturnOrBuyerSendOutId,
          comment,
          isReturnToSeller: Number(reason) == -1,
        })
      }
      submitLabel={t('common.submit').toUpperCase()}
      submittable={submitR.kind !== 'Loading' && dueDate != null && reason != ''}
      body={body}
      containerStyle={{ width: 500 }}
      modalName="item_return_from_warehouse"
    />
  );
};

function getFormLink(link: string): string {
  return (
    '<a href=' +
    link +
    ' target="_blank" rel="noopener noreferrer" style="text-decoration:underline;color:#1976d2">' +
    'form' +
    '</a>'
  );
}

export default View;
